/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useEffect, useState } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import {
  CustomRangeModal,
  ClockOutTable,
  ClockInTable,
  HoursWorked,
  HoursWorkedTable,
  QueryContainer,
  OutlinedButton,
  SelectViewBy,
  SelectDepartment,
  AllEmployees,
} from '../index';
import { format, addDays } from 'date-fns';
import {
  computeTotalHoursById,
  getDates,
} from '../../utilities/totalHoursData';
import { sortByLastName } from '../../utilities/sortBy';
import { apiUrl } from '../../utilities/apiUrl';

const loadingStyle = {
  display: 'block',
  position: 'absolute',
  top: '45%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};

export const EmployeeReports = (props) => {
  const allEmpData = props.allEmpData;
  const activeEmpData = props.activeEmpData;
  const timesheetActive = props.timesheetActive;
  const cutoffData = props.cutoffData;
  const allEmpView = props.allEmpView;
  const employeeData = props.employeeList;
  const entryPerPage = 50; // number of total rows per page on table
  const [employeeList, setEmployeeList] = useState([]);
  const [timesheetList, setTimesheetList] = useState([]);
  const [pageCount, setPageCount] = useState(
    Math.floor(employeeList.length / entryPerPage)
  );
  const [viewBy, setViewBy] = useState('');
  //const [view, setView] = useState("topChart");
  const [view, setView] = useState('totalTable');
  const [department, setDepartment] = useState('');
  const [pickerModalOpen, setPickerModalOpen] = useState(false);
  const [startValue, setStartValue] = useState(null);
  const [endValue, setEndValue] = useState(null);
  const [cleanData, setCleanData] = useState([]);
  const [chartDateRange, setChartDateRange] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [timesheetRange, setTimesheetRange] = useState([]);

  useEffect(() => {
    setPickerModalOpen(true);
    if (view === 'topChart') {
      totalHoursWorked(
        cutoffData.map((time) => ({
          ...time,
          ...employeeData.find((emp) => time.empid === emp._id),
        }))
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cutoffData]);

  const handleDepartmantChange = (event) => {
    setDepartment(event.target.value);
  };

  // VIEW BY CHANGE HANDLER
  const handleViewChange = (event) => {
    let val = event.target.value;
    setViewBy(val);
    setView('');

    if (val === 'all') {
      setEmployeeList(sortByLastName(allEmpData));
    } else if (val === 'checkedin') {
      setEmployeeList(sortByLastName(activeEmpData));
    } else if (val === 'checkedout') {
      // only get timesheet entries that are checked out today
      const today = format(new Date(), 'MM/dd/yyyy');
      const filterData = timesheetActive.filter((a) => {
        const checkout = format(new Date(a.checkout), 'MM/dd/yyyy');

        return today === checkout;
      });

      setTimesheetList(filterData);
      setPageCount(Math.floor(filterData.length / entryPerPage));
    }
  };

  // COMPUTE TOTAL HOURS WORK PER EMPLOYEE
  const totalHoursWorked = (data) => {
    setChartData([]);
    data.forEach((data) => {
      let hours = Math.floor((data.roundedtime / 60) * 1000) / 1000;
      chartData.push({
        empid: data.empid,
        deptid: data.deptid,
        name: `${data.firstname} ${data.lastname}`,
        hours: hours,
      });
    });

    setChartDateRange(getDates(data));
    setCleanData(
      computeTotalHoursById(
        chartData.map((data) => ({
          ...data,
          ...props.departmentList.find((dept) => dept._id === data.deptid),
        }))
      )
    );
  };

  // CUSTOM DATE FOR THE TOTAL HOURS WORKED TABLE AND CHART
  const handleCustomDate = () => {
    // Set hours of custom date range for start and end date when displaying timesheet
    startValue.setHours(0, 0, 0, 0);
    endValue.setHours(23, 59, 59, 99);

    // CONVERT TIME QUERY IN ISOSTRING TO OFFSET TIMEZONE
    const startValueWithTimeZoneOffset = startValue.toISOString();
    const endValueWithTimeZoneOffset = endValue.toISOString();

    fetch(
      apiUrl(
        `/timesheet?startdate=${startValueWithTimeZoneOffset}&enddate=${endValueWithTimeZoneOffset}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((response) => response.json())
      .then((data) => {
        const noNullCheckOuts = data.data.filter((data) => {
          return data.checkout !== null;
        });

        const dataRange = noNullCheckOuts.map((time) => ({
          ...time,
          ...employeeData.find((emp) => time.empid === emp._id),
        }));

        totalHoursWorked(dataRange);
        setTimesheetRange(dataRange);
        setPickerModalOpen(false);
        setViewBy('');
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <>
      <QueryContainer>
        {/* <Grid item sx={{ display: "flex", alignItems: "center" }}>
          <Grid item>
            <SelectViewBy viewBy={viewBy} handleViewChange={handleViewChange} />
          </Grid>
          <Grid item sx={{ ml: 3 }}>
            <SelectDepartment
              departmentList={props.departmentList}
              department={department}
              handleDepartmantChange={handleDepartmantChange}
            />
          </Grid>
        </Grid> */}
        <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid item>
            {/* <OutlinedButton [EPSv5_Dashboard-EmployeeReportsButton]
              onClick={() => {
                setPickerModalOpen(true);
                setViewBy("");
                setView("totalTable");
                setPageCount(Math.floor(cleanData.length / entryPerPage));
              }}
              btnTitle="Total Hours Worked"
              style={{
                width: 250,
                bgcolor: view === "totalTable" ? "#84D8C5" : "none",
                color: view === "totalTable" ? "#FFF" : "#84D8C5",
                "&:hover": {
                  color: "#FFF",
                  borderColor: "#84d8c5",
                  bgcolor:
                    view === "totalTable"
                      ? "#84d8c5"
                      : "rgb(132, 216, 197, 0.2) !important",
                },
              }}
            /> */}
            <OutlinedButton
              onClick={() => {
                setPickerModalOpen(true);
                setViewBy('');
                setView('totalTable');
                setPageCount(Math.floor(cleanData.length / entryPerPage));
              }}
              btnTitle="Choose Date Range"
              style={{
                width: 250,
                color: '#84D8C5',
                '&:hover': {
                  color: '#FFF',
                  borderColor: '#84d8c5',
                  bgcolor:
                    view === 'totalTable'
                      ? '#84d8c5'
                      : 'rgb(132, 216, 197, 0.2) !important',
                },
              }}
            />
          </Grid>
          {/* <Grid item sx={{ ml: 3 }}>
            <OutlinedButton
              onClick={() => {
                setPickerModalOpen(true);
                setViewBy("");
                setView("totalChart");
              }}
              btnTitle="Total Hours Worked (Chart)"
              style={{
                width: 250,
                bgcolor: view === "totalChart" ? "#84D8C5" : "none",
                color: view === "totalChart" ? "#FFF" : "#84D8C5",
                "&:hover": {
                  color: "#FFF",
                  borderColor: "#84d8c5",
                  bgcolor:
                    view === "totalChart"
                      ? "#84d8c5"
                      : "rgb(132, 216, 197, 0.2) !important",
                },
              }}
            />
          </Grid>
          <Grid item sx={{ ml: 3 }}>
            <OutlinedButton
              onClick={() => {
                setViewBy("");
                setView("topChart");
                // totalHoursWorked(cutoffData);
              }}
              btnTitle="Top of the Charts"
              style={{
                width: 250,
                bgcolor: view === "topChart" ? "#84D8C5" : "none",
                color: view === "topChart" ? "#FFF" : "#84D8C5",
                "&:hover": {
                  color: "#FFF",
                  borderColor: "#84d8c5",
                  bgcolor:
                    view === "topChart"
                      ? "#84d8c5"
                      : "rgb(132, 216, 197, 0.2) !important",
                },
              }}
            />
          </Grid> */}
        </Grid>
      </QueryContainer>
      <CircularProgress
        sx={props.isLoading ? loadingStyle : { display: 'none' }}
      />
      {viewBy === 'all' ? (
        <AllEmployees data={allEmpView} />
      ) : viewBy === 'checkedin' ? (
        <ClockInTable
          departmentList={props.departmentList}
          activeEmpData={activeEmpData}
          timesheetActive={timesheetActive}
        />
      ) : viewBy === 'checkedout' ? (
        <ClockOutTable
          checkedOutTimesheet={timesheetList}
          allEmpData={allEmpData}
          pageCount={pageCount}
        />
      ) : view === 'totalTable' ? (
        <HoursWorkedTable
          data={cleanData}
          timesheetRange={timesheetRange}
          pageCount={pageCount}
          startValue={startValue}
          endValue={endValue}
        />
      ) : view === 'totalChart' ? (
        <HoursWorked
          cleanData={cleanData}
          title={'Total Hours Worked Per Employee'}
          fromDate={
            startValue === null
              ? chartDateRange[0]
              : format(new Date(startValue), 'dd MMM yyyy')
          }
          toDate={
            endValue === null
              ? chartDateRange[chartDateRange.length - 1]
              : format(new Date(endValue), 'dd MMM yyyy')
          }
        />
      ) : (
        <HoursWorked
          cleanData={cleanData.sort((a, b) => a.hours - b.hours)}
          fromDate={chartDateRange[0]}
          toDate={chartDateRange[chartDateRange.length - 1]}
          title={'Top of the Charts'}
        />
      )}
      <CustomRangeModal
        open={pickerModalOpen}
        onClose={() => setPickerModalOpen(false)}
        startValue={startValue}
        startOnChange={(newValue) => setStartValue(newValue)}
        endValue={endValue}
        endOnChange={(newValue) => setEndValue(newValue)}
        primaryClick={handleCustomDate}
        secondaryClick={() => setPickerModalOpen(false)}
      />
    </>
  );
};
